import React, { FC, Ref, forwardRef } from 'react';

import { Box, Typography } from '@mui/material';
import { styled } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';

import CloseIcon from '~/assets/icons/CloseIcon';
import CustomButton from '~/components/UI/CustomButton/CustomButton';
import { useTenantContext } from '~/context/TenantProvider';
import useTenantTranslation from '~/hooks/useTenantTranslation';
import { LOGIN_ROUTE } from '~/routes';
import { SMALL_MARGIN_PX } from '~/theme';
import { TENANTS_CONFIG, TenantsRefs } from '~/utils/tenantsConfig';

interface JoinNowModalProps {
  isLoggedIn: boolean;
  handleClose: () => void;
}

const JoinNowModal: FC<JoinNowModalProps> = forwardRef(
  ({ handleClose, isLoggedIn }, ref: Ref<HTMLDivElement>) => {
    const { t } = useTenantTranslation();
    const { tenant } = useTenantContext();
    const location = useLocation();
    const joinNowBlockConfig = TENANTS_CONFIG[tenant?.name || TenantsRefs.Fu]?.joinNowBlock;

    return (
      <Wrapper tabIndex={-1} ref={ref}>
        <Container data-testid="joinNowModal">
          <CloseBtn onClick={handleClose} data-testid="closeJoinNowModal" />
          <Title variant="h2" data-testid="joinModalTitle">
            {t(joinNowBlockConfig?.titleKey)}
          </Title>
          <Text>{t(joinNowBlockConfig.descriptionKey)}</Text>
          <ButtonsWrapper>
            {isLoggedIn ? (
              <Link to={joinNowBlockConfig?.link} data-testid="joinModalStartFreeTrial">
                <JoinBtn>{t(joinNowBlockConfig.btnCaptionKey)}</JoinBtn>
              </Link>
            ) : (
              <>
                <Link to={joinNowBlockConfig?.link} data-testid="joinModalStartFreeTrial">
                  <JoinBtn>{t(joinNowBlockConfig.btnCaptionKey)}</JoinBtn>
                </Link>
                <Link
                  to={{
                    pathname: LOGIN_ROUTE,
                  }}
                  state={{ prevPath: location.pathname, prevSearch: location.search }}
                  data-testid="joinModalLogin"
                >
                  <LoginBtn color="secondary" variant="contained">
                    {t('button.logIn')}
                  </LoginBtn>
                </Link>
              </>
            )}
          </ButtonsWrapper>
        </Container>
      </Wrapper>
    );
  },
);

export default JoinNowModal;

const Wrapper = styled('div')`
  display: flex;
  align-items: center;
  background: ${({ theme }) => alpha(theme.palette.common.blueDark, 0.85)};
  width: 100%;
  height: 100%;
`;
const Container = styled(Box)`
  max-width: 532px;
  margin: 0 auto;
  padding: 37px 36px;
  position: relative;

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: 37px 16px;
  }
`;
const CloseBtn = styled(CloseIcon)`
  cursor: pointer;
  position: absolute;
  right: 10px;
  top: 0;
  width: 20px;
  height: 20px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    right: 0;
  }
`;
const Title = styled(Typography)`
  && {
    font-size: 18px;
    text-align: center;
    margin-bottom: 16px;

    ${({ theme }) => theme.breakpoints.up('md')} {
      font-size: 26px;
      margin-bottom: 53px;
    }
  }
`;
const Text = styled(Typography)`
  color: ${({ theme }) => theme.palette.common.gray};
  font-size: 18px;
  line-height: 1.35;
  text-align: center;
  margin-bottom: 24px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 22px;
    margin-bottom: 63px;
  }
`;
const ButtonsWrapper = styled(Box)`
  display: flex;
  justify-content: center;
  flex-direction: column;

  ${({ theme }) => theme.breakpoints.up('sm')} {
    flex-direction: row;
  }
`;
const JoinBtn = styled(CustomButton)`
  && {
    width: 100%;
  }
`;
const LoginBtn = styled(CustomButton)`
  && {
    margin-right: 0;
    padding: 19px 45px;
    width: 100%;
    margin-top: ${SMALL_MARGIN_PX};

    ${({ theme }) => theme.breakpoints.up('sm')} {
      margin-left: 14px;
      margin-top: 0;
    }

    ${({ theme }) => theme.breakpoints.up('md')} {
      padding: 15px 42px;
    }
  }
`;
